import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 't3ft_admin_token';

const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [trustStats, setTrustStats] = useState([]);
  const [tipsters, setTipsters] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const apply = useCallback((data) => {
    setTrustStats(data.trust_stats || []);
    setTipsters(data.tipsters || []);
    setEntries(data.entries || []);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get('/content');
    apply(data);
  }, [apply]);

  useEffect(() => {
    refresh().catch(err => console.warn('[content] load failed', err?.message)).finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (username, password) => {
    try {
      const { data } = await api.post('/auth/login', { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      return true;
    } catch { return false; }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const resetAll = useCallback(async () => {
    const { data } = await api.post('/content/reset');
    apply(data);
  }, [apply]);

  const updateTrustStat = useCallback(async (idx, patch) => {
    await api.put(`/trust-stats/${idx}`, patch);
    setTrustStats(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }, []);

  const updateTipster = useCallback(async (tipster) => {
    await api.put(`/tipsters/${tipster.id}`, tipster);
    setTipsters(prev => prev.map(t => (t.id === tipster.id ? tipster : t)));
  }, []);

  const addEntry = useCallback(async (entry) => {
    await api.post('/entries', entry);
    await refresh();
  }, [refresh]);

  const updateEntry = useCallback(async (id, entry) => {
    await api.put(`/entries/${id}`, entry);
    await refresh();
  }, [refresh]);

  const deleteEntry = useCallback(async (id) => {
    await api.delete(`/entries/${id}`);
    setEntries(prev => prev.filter(e => e.id !== id));
  }, []);

  const entriesFor = useCallback((tipsterId) => entries.filter(e => e.tipster_id === tipsterId), [entries]);

  const value = useMemo(() => ({
    trustStats, tipsters, entries, entriesFor, loading, authed: !!token,
    login, logout, resetAll, updateTrustStat, updateTipster, addEntry, updateEntry, deleteEntry
  }), [trustStats, tipsters, entries, entriesFor, loading, token, login, logout, resetAll,
    updateTrustStat, updateTipster, addEntry, updateEntry, deleteEntry]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside DataProvider');
  return ctx;
}
